<template>
	<div v-if="items?.length" class="context-list-item">

		<transition-group name="list" tag="div">
			<div v-for="(item, index) of filteredItems" :key="params.zone+'-key-'+(item.auth||item.id)"
				 class="context-list-item__item">

				<div class="context-list-item__droppable"
					 :key="params.zone+'-dummy-droppable-context-before-'+item.id">
					<draggable :id="params.zone+'-dummy-droppable-context-before-'+item.id"
							   :item="item.auth||item.id"
							   :draggable="false"
							   :droppable="isItemDraggable(item)"
							   :accepts-zones="[params.zone]"
							   :rejects-ids="rejectIdsFor(item, index)"
							   :preference="1"
							   :zone="params.zone"
							   class="context-list-item__droppable-zone"
							   :drop-processor="onDropOverPos"/>
				</div>

				<draggable :id="item.auth||item.id"
						   :item="payloadOfContext(item)"
						   :zone="params.zone"
						   :draggable="isItemDraggable(item)"
						   :droppable="false">
					<template v-slot:default="{onMouseDown}">
						<div class="context-list-item__element" :class="classOfElement(item)">
								<context-color :item="item" :readonly="isReadonly"/>
							<component
								:is="componentOf()"
								:to="routeOfListItem(item)"
								draggable="false" class="__link">

								<div @click="onBeforeRouting(item)" class="__content">
									<v-btn v-if="isContextWithChildren(item)" class="__opener" icon small
										   @click="onToggleOpenContext(item, true)">
										<v-icon small>{{ openerIconOfContext(item) }}</v-icon>
									</v-btn>
									<div v-else-if="params.withChildren" class="__opener">
										&nbsp;
									</div>
									<div class="__icon">
										<loader
											v-if="item.contextId && item.contextId===pending.context?.contextId"
											small/>
										<avatar v-else-if="shouldBeWithAvatar(item)" :user="userOf(item)" small/>
										<template v-else-if="shouldBeWithLogo(item)">
											<logo :item="item" small :class="{'__logo': hasQuickTags(item)}"/>
											<v-btn v-if="hasQuickTags(item)" class="__quick-tags-toggler" icon x-small
												   :title="isQuickTagsOpen(item) ? 'Свернуть быстрые теги' : 'Показать быстрые теги'"
												   @click.prevent.stop="onToggleQuickTags(item)">
												<v-icon small>
													{{ isQuickTagsOpen(item) ? "mdi-chevron-down" : "mdi-chevron-right" }}
												</v-icon>
											</v-btn>
										</template>
										<v-icon v-else small
												:color="colorOfContextIcon(item)"
												@click.prevent.stop="onToggleOpenContext(item, true)">
											{{ iconOfContext(item) }}
										</v-icon>
									</div>
									<span class="__name">
										{{ nameOf(item) }}
										<tag
											v-if="isNewItem(item)"
											:class="COLOR_NEW" class="ml-1" dark title="Новый проект!">new</tag>
									</span>
									<context-progress :item="item" compact :height="4"/>
								</div>
							</component>

							<context-menu-btn v-if="!isReadonly" :item="item" small class="__item-btn"/>
						</div>

						<div v-if="isItemDraggable(item)" class="draggable__handle"
							 @mousedown.prevent.stop="onMouseDown">
							<v-icon>mdi-drag-vertical</v-icon>
						</div>
					</template>
				</draggable>

				<context-quick-tags v-if="hasQuickTags(item)" :item="item" :open="isQuickTagsOpen(item)"/>
			</div>
		</transition-group>

		<div class="context-list-item__droppable" :key="params.zone+'-dummy-droppable-last'">
			<draggable :id="params.zone+'-dummy-droppable-context-last'"
					   item="last"
					   :draggable="false"
					   :droppable="true"
					   :accepts-zones="[params.zone]"
					   :rejects-ids="rejectIdsFor(filteredItems?.length?filteredItems[filteredItems.length-1]:null, null)"
					   :preference="1"
					   :zone="params.zone"
					   class="context-list-item__droppable-zone"
					   :drop-processor="onDropOverPos"/>
		</div>
	</div>
</template>

<script>
import draggable from '@/components/elements/draggable2';
import storage from "@/utils/storage";
import Config from "@/config";
import {iso2Date} from "@/utils/date";
import Vue from "vue";
import base from "@/mixins/base";
import {PRESETS} from "@/utils/pick";
import {isPannelViewOfContextId, parseChain} from "@/utils/panels";
import ContextMenuBtn from "@/components/context/context-menu-btn";
import vp from "@/mixins/vp";
import ContextProgress from "@/components/context/context-progress";
import ContextQuickTags from "@/components/context/context-quick-tags";
import ContextColor from "@/components/context/context-color";

/**
 * Список строк контекстов (проекты, подборки, пользователи) с drag&drop и быстрыми тегами
 */
export default {
	name: "context-list-item",
	components: {ContextColor, ContextQuickTags, ContextProgress, ContextMenuBtn, draggable},
	props: {
		items: {},
		params: {},
	},
	mixins: [base, vp],
	data() {
		return {
			/** Id групп с раскрытыми быстрыми тегами (хранится в local storage) */
			openQuickTagIds: storage.get(Config.STORAGE_OPEN_QUICK_TAGS) || [],
		}
	},
	computed: {
		isReadonly() {
			return this.params.readonly;
		},
		filteredItems() {
			const items = this.items;

			if (this.ui.search.withContexts) {
				const re = new RegExp(this.search, "i");

				const matchesSearch = (context) => {
					const matches = context.name?.match(re)
						// for user picks
						|| context.nick?.match(re)
						|| context.firstName?.match(re)
						|| context.lastName?.match(re);
					if (matches) return true;

					// check children
					return !!context.children?.find(el => matchesSearch(el));
				}

				return items?.filter(el => matchesSearch(el));
			}
			if (this.ui.withArchivedGroups) return items;

			return items?.filter(el => !el.archivedAt);
		},
		selectedContextId() {
			// important to use contextId from params as context-list-item.vue may be used not only with current context
			return this.params.item?.contextId || this.params.item?.id;
		},
	},
	methods: {
		onDropOverPos(source, destination, index) {
			return new Promise(async (resolve, reject) => {
				console.log("onDropOverPos", source, destination);
				await this.do('context/pos', {
					contextId: source.item.contextId,
					beforeContextId: destination.item,
					items: this.items,
					prop: this.params.userSortProp,
					sorter: this.params.sorter,
				});
				resolve();
			});
		},
		async onToggleOpenContext(context, isIconClick = false) {
			if (context.isGroup && typeof context.children === "undefined" && !context.isOpen) {
				Vue.set(context, "isOpen", true);
			} else if (context.isOpen) {
				// close only if same context clicked twice or icon clicked
				if (this.params.item?.contextId === context.contextId || isIconClick) Vue.set(context, "isOpen", false);
			} else {
				// always open closed contexts on click
				Vue.set(context, "isOpen", true);
			}
		},
		/** Открыт ли быстрый тег этой группы (?tag=<id> в URL) */
		hasSelectedQuickTag(item) {
			const tagId = this.$route.query.tag;
			return !!tagId && this.selectedContextId === item.id && !!item.quickTags?.some(el => el.contextId === tagId);
		},
		/** Быстрые теги есть только у групп (проектов) */
		hasQuickTags(item) {
			return !!item.isGroup && !!item.quickTags?.length;
		},
		isQuickTagsOpen(item) {
			return this.openQuickTagIds.includes(item.id);
		},
		/** Раскрывает/сворачивает быстрые теги группы и запоминает состояние в local storage */
		setQuickTagsOpen(item, isOpen) {
			if (this.isQuickTagsOpen(item) === isOpen) return;
			this.openQuickTagIds = isOpen
				? [...this.openQuickTagIds, item.id]
				: this.openQuickTagIds.filter(id => id !== item.id);
			this.commit("uiStoreIdInList", {key: Config.STORAGE_OPEN_QUICK_TAGS, id: item.id, value: isOpen});
		},
		onToggleQuickTags(item) {
			this.setQuickTagsOpen(item, !this.isQuickTagsOpen(item));
		},
		/**
		 * Маршрут элемента списка контекстов. В панельном режиме цепочка сводится к одному id (последнему),
		 * чтобы открывалась только первая панель, а остальные сбрасывались.
		 */
		routeOfListItem(item) {
			const route = this.routeOfContext(item);
			// проект с включённым withPannelView открываем через цепочку контекстов (роут context-chain)
			if (item.isGroup && route?.name === "context" && isPannelViewOfContextId(route.params.contextId)) {
				const chain = parseChain(route.params.contextId);
				return {name: "context-chain", params: {contextIdChain: chain[chain.length - 1]}, query: route.query};
			}
			if (route?.name === "context" && isPannelViewOfContextId(route.params.contextId)) {
				const chain = parseChain(route.params.contextId);
				return {...route, params: {...route.params, contextId: chain[chain.length - 1]}};
			}
			return route;
		},
		onBeforeRouting(context) {
			const isSameContext = !!context.contextId && context.contextId === this.params.item?.contextId;

			if (isSameContext) {
				// clicked same context

				if (!this.isMD) {
					// TODO move logic to ui mutations
					// this is mobile - just hide context menu
					this.commit("uiToggleContextList");
				}

				return;
			}

			this.params.item = context;

			if (typeof this.params.onClick === "function") {
				this.params.onClick(context);
				return;
			}

			this.commit("uiSetPendingContext", context);
		},
		componentOf() {
			if (typeof this.params.onClick === "function") return "div";
			return "router-link";
		},
		rejectIdsFor(item, index) {
			// 1. reject this item
			const rejectIds = [];
			if (item) rejectIds.push(item.auth || item.id);
			// 2. reject prev item
			if (index) {
				const prev = this.filteredItems[index - 1];
				rejectIds.push(prev.auth || prev.id);
			}
			return rejectIds;
		},
		payloadOfContext(item) {
			return {
				name: item.name,
				contextId: item.auth || item.id,
			};
		},
		isItemDraggable(item) {
			if (this.isReadonly) return false;
			if (this.params.onClick) return false;
			if (typeof this.params.withDND == "function") return this.params.withDND(item);
			return this.params.withDND;
		},
		isContextWithChildren(item) {
			return this.params.withChildren
				&& (
					item.isGroup
					|| (item.isFolder && item.children?.length)
				);
		},
		shouldBeWithAvatar(item) {
			return item.avatarName
				|| item.avatarUserId
				|| item.avatar
				|| item.isUser;
		},
		shouldBeWithLogo(item) {
			return item.shouldBeWithLogo
				|| item.image
				|| item.isGroup;
		},
		nameOf(item) {
			if (item.isUser) return this.nickOf(item);
			if (item.isTag) return item.name.toLowerCase();

			let text = item.title || item.name;

			if (item.isPick && item.preset === PRESETS.EVENTS && this.area.countUnreadEvens) {
				text += " (" + this.area.countUnreadEvens + ")";
			}
			return text;
		},
		userOf(item) {
			if (item.isUser) return item;
			if (item.isPick) {
				if (this.user.id === item.creatorId) return this.user;
				return this.userById(item.creatorId);
			}
			return null;
		},
		iconOfContext(item) {
			if (item.isPick && item.preset === PRESETS.EVENTS) {
				if (this.area.countUnreadEvens) return "mdi-bell";
				return "mdi-bell-outline";
			}
			if (item.isTag) return "mdi-pound";
			if (item.emoji) return item.emoji;
			if (item.icon) return item.icon;
			return 'mdi-folder-outline';
		},
		colorOfContextIcon(item) {
			if (item.isPick && item.preset === PRESETS.EVENTS) {
				if (this.area.countUnreadEvens) return Config.COLORS.find(el => el.css === "--color15")?.icon;
				return Config.COLORS.find(el => el.css === "--color19")?.icon;
			}
			if (!item.color) return null;
			const color = Config.COLORS.find(el => el.css === item.color);
			return color?.icon;
		},
		classOfElement(item) {
			const c = [];
			if (item.color) c.push(item.color);
			if (this.selectedContextId === item.id) c.push("--selected");
			if (this.hasSelectedQuickTag(item)) c.push("--with-selected-tag");
			if (item.isGroup && item.imData?.length) {
				c.push("--with-im");
			}
			if (!!item.archivedAt) {
				c.push("--archived");
			}
			return c.length ? c : null;
		},
		openerIconOfContext(item) {
			return item.isOpen ? "mdi-chevron-down" : "mdi-chevron-right";
		},
		isNewItem(item) {
			const msDifference = new Date() - iso2Date(item.createdAt);
			return !isNaN(msDifference) && msDifference < 24 * 60 * 60 * 1000;
		},
	}
}
</script>

<style lang="scss">
.context-list-item {

	&__element {
		position: relative; // ручка перетаскивания позиционируется внутри строки
		display: flex;
		// +4px - отступ от ручки перетаскивания, +4px - место под цветовую полосу (context-color, раньше была border-left)
		padding: 0 $min-padding 0 calc(#{$general-padding} + 16px);
		@include transition();

		&:hover {
			background: map-get($grey, "lighten-3");
			opacity: 1 !important;

			.__link {
				opacity: 1 !important;
				color: $black !important;
			}

			.__item-btn {
				opacity: 1;
			}
		}

		.__link {
			color: $default-text-color !important;;
			display: flex;
			flex: 1;
			line-height: 1.3rem;
			cursor: pointer;

			.__content {
				flex: 1;
				display: flex;
				align-items: flex-start;
				gap: $general-padding;

				.__opener {
					position: relative;
					width: 24px;
					height: 24px;
					margin-top: 4px;
					flex-shrink: 0;
					display: flex;
					justify-content: center;
					align-items: center;
				}

				.__icon {
					position: relative;
					width: 24px;
					height: 24px;
					margin-top: 5px;
					flex-shrink: 0;
					display: flex;
					justify-content: center;
					align-items: center;

					// переключатель быстрых тегов появляется вместо логотипа группы при наведении на сам логотип
					.__quick-tags-toggler {
						position: absolute;
						opacity: 0;
						pointer-events: none;
					}

					&:hover {
						.__logo {
							opacity: 0;
						}

						.__quick-tags-toggler {
							opacity: 1 !important;
							pointer-events: auto;
						}
					}
				}

				.__name {
					flex: 1;
					min-height: 32px;
					padding: 8px 0;
					font-size: $font-size-3;
				}
			}
		}

		// выбран быстрый тег группы: сама группа выделена мягче
		&.--selected.--with-selected-tag {
			background: map-get($grey, "lighten-5");
		}

		&.--selected {
			background: white;

			.__name {
				color: black;
			}
		}

		&.--archived {
			.__link {
				opacity: $default-disabled-opacity;
			}
		}
	}

	.context-progress {
		max-width: 20%;
		margin-top: 15px;
		margin-right: 4px;
	}

	.__item-btn {
		margin-top: 5px;
		width: 24px !important;
		height: 24px !important;
		opacity: 1;

		@include up($sm) {
			opacity: 0;
		}
	}

	.draggable__handle {
		left: 6px;
		top: 50%;
		transform: translateY(-50%);
		height: 100%;
		display: flex;
		align-items: center;
	}

	&__droppable {
		z-index: 2;
		pointer-events: none;
		position: relative;
		width: 100%;
		height: 0;
		@include transition();

		&-zone {
			z-index: 1;
			position: absolute;
			width: 100%;
			height: 20px;
			left: 0;
			top: 0;
			transform: translateY(-50%);
		}

		&:last-child {
			margin-bottom: 8px;
		}
	}
}
</style>
